'use client';
import { LoaderPlaceholder } from '@/components/Placeholder';
import { formatDate, getClassNames } from '@/utils/helpers';
import {
  Button,
  Label,
  List,
  ListItem,
  Status,
} from '@yaradigitallabs/ahua-react';
import type React from 'react';
import type { MaterialWithSoftTag } from '@/features/match-maker/utils/match-maker.types';

type CalculationCardForDrawerProps = {
  tag: MaterialWithSoftTag;
  tagValues?: {
    value: number;
    unit: string;
    timestamp: string;
    hasError?: boolean;
  };
  isLoading?: boolean;
  className?: string;
  onDetailSelect?: () => void;
};

export const CalculationCardForDrawer: React.FC<
  CalculationCardForDrawerProps
> = ({ tag, tagValues, isLoading = false, className, onDetailSelect }) => {
  const { tag_name: title, tag_description: description, priority } = tag;
  const value = tagValues?.value ?? '-';
  const unit = tagValues?.unit ?? tag.unit;
  const timestamp = tagValues?.timestamp
    ? formatDate(tagValues.timestamp)
    : '-';
  const numericValue = typeof value === 'number' ? value : Number(value);
  const formattedValue = Number.isFinite(numericValue)
    ? numericValue.toFixed(2)
    : value;
  return (
    <div
      className={getClassNames(
        'rounded py-3 pb-2 flex flex-col justify-start gap-2',
        className ? className : ''
      ).trim()}
    >
      <div className="pb-2 flex flex-col items-start gap-2 border-b border-gray-200">
        {priority !== undefined && priority > 1 && (
          <Status size="n">{priority}</Status>
        )}
        {priority === 1 && (
          <Status size="n" colorType="purple">
            Primary
          </Status>
        )}
        <div className="flex flex-col justify-start">
          <Label maxLineCount={1} size="l" htmlFor={title}>
            {title}
          </Label>
          {description && (
            <span className="text-xs font-normal text-gray-500 text-wrap line-clamp-2">
              {description}
            </span>
          )}
        </div>
      </div>
      <div className="flex justify-between">
        <div className="flex justify-start items-center gap-4">
          {isLoading ? (
            <LoaderPlaceholder className="w-10! rounded-lg!" size="n" />
          ) : tagValues?.hasError ? (
            <Status size="s" colorType="red">
              Error
            </Status>
          ) : (
            <div className="px-3 py-1 rounded-lg flex justify-between items-center gap-4 border border-dashed border-gray-300 bg-gray-100">
              <>
                {formattedValue && (
                  <span className="text-md font-semibold text-gray-900">
                    {formattedValue}
                  </span>
                )}
                {unit && <span className="text-sm text-gray-500">{unit}</span>}
              </>
            </div>
          )}

          <span className="text-xs text-gray-500 font-normal flex gap-4">
            Last update:&nbsp;
            {isLoading ? (
              <LoaderPlaceholder
                className="inline-block w-16! rounded-lg!"
                size="n"
              />
            ) : (
              timestamp
            )}
          </span>
        </div>
        <div>
          <Button variant="ghost" size="xs" onClick={onDetailSelect}>
            Details
          </Button>
        </div>
      </div>
    </div>
  );
};

export const CalculationCardSkeletonComponent = () => {
  return (
    <List.Middle>
      <ListItem>
        <ListItem.BodyTail divider>
          <ListItem.Body>
            <ListItem.Subtitle>
              <LoaderPlaceholder className="w-[200px]! rounded-lg!" />
            </ListItem.Subtitle>
            <ListItem.Caption>
              <LoaderPlaceholder className="mt-3 rounded-lg!" />
            </ListItem.Caption>
          </ListItem.Body>
          <ListItem.Tail className="items-end! gap-2!">
            <LoaderPlaceholder className="rounded-lg!" />
            <LoaderPlaceholder className="rounded-lg!" />
          </ListItem.Tail>
        </ListItem.BodyTail>
      </ListItem>
    </List.Middle>
  );
};
